import { ROUTING_STATUS, type RoutingDesk } from './routingTypes';

/*
  The Routing GROUP groups NAVIGATION, not routes — Receiving and Dispatching are sibling modules,
  so each keeps its own path. The document-type catalog is reached through ONE Settings menu, its
  heavier consumer's (Receiving), and nests there. The list sits at `/documents` so its leaf does
  not prefix the Settings path — the rail lights an entry by prefix.
*/
export const RECEIVING_ROOT = '/document-receiving';

export const RECEIVING_PATH = `${RECEIVING_ROOT}/documents`;

export const DISPATCHING_PATH = '/document-dispatching';

/** Where a dispatch to a unit arrives — a sibling module, with its own path. */
export const INBOX_PATH = '/document-inbox';

export const ROUTING_SETTINGS_PATH = `${RECEIVING_ROOT}/settings`;

/** The server's refusal for an account linked to no employee with a post — one wording. */
export const NO_DESK =
  "Your account is not linked to an employee with a current post, so you cannot route, acknowledge or cancel. An administrator links it from the employee's record.";

/**
 * Why the CALLER cannot route a document on from a LIST row, or null when they can — the server's
 * own refusals, said BEFORE the click (a server guard is not an affordance; both are owed).
 *
 * **Routing is a UNIT's act** (10 Oct 2026): it leaves from the unit of the caller's post, which
 * must hold it. Routing again while an earlier routing is open is allowed. A profile reads the
 * server's per-caller `canRoute` instead; a list row has no such field, so this mirrors it from
 * the caller's desk. Until the desk has loaded it says nothing — the server still decides.
 */
export function routeDisabledReason(
  row: { isActive: boolean; status: number; holders: { id: string }[] },
  desk: RoutingDesk | null,
): string | null {
  if (!row.isActive) return 'This document is deleted. Restore it before routing it.';
  if (row.status === ROUTING_STATUS.dispatched || row.holders.length === 0) return 'It has been dispatched. Its route ends here.';
  if (desk === null) return null;
  if (desk.orgUnitId == null) return NO_DESK;
  if (!row.holders.some((h) => h.id === desk.orgUnitId)) return 'It is not with your unit. Only a unit holding it routes it on.';

  return null;
}

/**
 * Why a dispatch cannot be deleted, or null when it can — the server's refusal, said first. Once
 * the unit it went to has received it, it is that unit's record of what arrived.
 */
export function dispatchDeleteDisabledReason(row: { isActive: boolean; inboxReceivedAt: string | null }): string | null {
  if (!row.isActive) return 'This dispatch is already deleted.';
  if (row.inboxReceivedAt != null) return 'The unit it was sent to has received it, so it can no longer be deleted.';

  return null;
}

/**
 * Whether the CALLER is the one to receive an Inbox item — posted to the unit it was sent to. Who
 * you ARE is not a state a button can explain, so a row that is not yours offers no Receive at all.
 */
export function isInboxRecipient(row: { recipientOrgUnitId: string | null }, desk: RoutingDesk | null): boolean {
  return desk?.orgUnitId != null && row.recipientOrgUnitId === desk.orgUnitId;
}

/** Why an Inbox item cannot be received, or null when it can — still on its way, and never twice. */
export function inboxReceiveDisabledReason(row: { isActive: boolean; inboxReceivedAt: string | null }): string | null {
  if (!row.isActive) return 'The sending unit deleted this dispatch.';

  return row.inboxReceivedAt != null ? 'It has already been received.' : null;
}

/** Why the CALLER cannot route an Inbox item on from a list row — received first, then as a received document. */
export function inboxRouteDisabledReason(
  row: { isActive: boolean; inboxReceivedAt: string | null; status: number; holders: { id: string }[] },
  desk: RoutingDesk | null,
): string | null {
  if (row.isActive && row.inboxReceivedAt == null) return "Receive it first — it is the sending unit's until it is taken in.";

  return routeDisabledReason(row, desk);
}
